import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { ROLE_TYPE_FAMILY_MEMBER } from "../../../constants/RegisterConstants";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { RouterAddress } from "../../../constants/RouterAddress";
import { getErrorTitle } from "../../../constants/constants";
import {
  dashboardPageContainerStyle,
  dashboardSectionCardStyle,
  renderDashboardHeader,
} from "./dashboardStyles";
import {
  appAccentPanelStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
} from "../../../components/general/uiSurfaceStyles";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const DashboardFamilyMember = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const mobile = useResponsive().isMobile;
  const [loading, setLoading] = useState<boolean>(false);

  const userRoleType = authStore.getUserRoleType();
  const [userId, setUserId] = useState<string>(authStore.getUserId());

  const [familyResidents, setFamilyResidents] = useState<any[]>([]);
  const [familyResidentsLoading, setFamilyResidentsLoading] = useState<boolean>(false);
  const [expandedRows, setExpandedRows] = useState<any>(null);

  useEffect(() => {
    const fetchFamilyResidents = async () => {
      setFamilyResidentsLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getFamilyResidents();
        setFamilyResidents(response || []);
      } catch (error: any) {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error["message"], life: 3000 });
      } finally {
        setFamilyResidentsLoading(false);
      }
    };
    fetchFamilyResidents();
  }, [backendAPIStore]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await backendAPIStore.init();
        const authenticated = await authStore.isUserLoggedIn();
        if (authenticated) {
          setUserId(authStore.getUserId());
          const userDetails = await backendAPIStore.getUserProfile(authStore.getUserId());
          if (!userDetails) {
            navigate(RouterAddress.PROFILE);
          }
        } else {
          navigate(RouterAddress.LOGIN_PAGE);
          return;
        }
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [backendAPIStore, userId]);

  const sharedResidentsByProvider = useMemo(() => {
    const providerMap = new Map<string, any>();

    familyResidents.forEach((resident: any) => {
      const providerKey = String(
        resident?.providerId
        || resident?.actorLinkId
        || resident?.providerEmail
        || resident?.providerName
        || "shared-provider",
      ).trim().toLowerCase();

      const existingProvider = providerMap.get(providerKey) || {
        providerKey,
        providerName: resident?.providerName || "Shared Provider",
        providerEmail: resident?.providerEmail || "",
        residents: [],
      };

      existingProvider.providerName = existingProvider.providerName || resident?.providerName || "Shared Provider";
      existingProvider.providerEmail = existingProvider.providerEmail || resident?.providerEmail || "";
      existingProvider.residents.push(resident);
      providerMap.set(providerKey, existingProvider);
    });

    return Array.from(providerMap.values())
      .map((provider: any) => ({
        ...provider,
        residents: (provider.residents || []).sort((first: any, second: any) =>
          `${first?.lastName || ""} ${first?.firstName || ""} ${first?.residentName || ""}`.localeCompare(
            `${second?.lastName || ""} ${second?.firstName || ""} ${second?.residentName || ""}`,
          ),
        ),
      }))
      .sort((first: any, second: any) => String(first?.providerName || "").localeCompare(String(second?.providerName || "")));
  }, [familyResidents]);

  const providerRows = useMemo(() => (
    sharedResidentsByProvider.map((providerGroup: any) => ({
      providerId: providerGroup.providerKey,
      providerName: providerGroup.providerName || "Shared Provider",
      email: providerGroup.providerEmail || "",
      residentCount: providerGroup.residents.length,
      residents: providerGroup.residents,
    }))
  ), [sharedResidentsByProvider]);

  if (userRoleType && userRoleType !== ROLE_TYPE_FAMILY_MEMBER) {
    return <Unauthorized />;
  }

  const sourceTagStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "999px",
    padding: "0.15rem 0.55rem",
    fontSize: "0.74rem",
    fontWeight: 700,
    border: "1px solid #bae6fd",
    background: "#f0f9ff",
    color: "#0c4a6e",
  } as const;

  const residentsCountBody = (provider: any) => {
    const count = provider.residentCount ?? provider.residents?.length ?? 0;
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        minWidth: "1.6rem", height: "1.6rem", borderRadius: "999px",
        background: count > 0 ? "#eff6ff" : "#f1f5f9",
        border: count > 0 ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
        color: count > 0 ? "#185DA2" : "#94a3b8",
        fontWeight: 700, fontSize: "0.82rem", padding: "0 0.4rem",
      }}>
        {count}
      </span>
    );
  };

  const providerNameBody = (provider: any) => (
    <div>
      <div className="flex align-items-center gap-2 flex-wrap">
        <span style={{ fontWeight: 600 }}>{provider.providerName}</span>
        <span style={sourceTagStyle}>Shared</span>
      </div>
      {provider.email ? (
        <div style={{ color: "#64748b", fontSize: "0.85rem", marginTop: "0.2rem" }}>{provider.email}</div>
      ) : null}
    </div>
  );

  const rowExpansionTemplate = (provider: any) => {
    const residents = provider.residents || [];
    if (residents.length === 0) {
      return (
        <div className="p-3" style={{ color: "#64748b", fontStyle: "italic" }}>
          No residents have been shared with you under this provider yet.
        </div>
      );
    }
    return (
      <DataTable value={residents} dataKey="residentId" size="small" style={{ marginLeft: "3rem" }}>
        <Column
          header="Name"
          body={(r: any) => (
            <div className="flex align-items-center gap-2 flex-wrap">
              <span style={{ fontWeight: 600 }}>
                {`${r.firstName || ""} ${r.lastName || ""}`.trim() || r.residentName || "Resident"}
              </span>
              <span style={sourceTagStyle}>Shared</span>
              <span
                style={{
                  ...sourceTagStyle,
                  border: r?.sharedAccess ? "1px solid #bbf7d0" : "1px solid #fde68a",
                  background: r?.sharedAccess ? "#f0fdf4" : "#fffbeb",
                  color: r?.sharedAccess ? "#166534" : "#92400e",
                }}
              >
                {r?.sharedAccess ? "Ready" : "Pending"}
              </span>
            </div>
          )}
          style={{ fontWeight: 600 }}
        />
      </DataTable>
    );
  };

  return (
    <div className="container-fluid p-3">
      <Toast ref={toast} />
      <div style={dashboardPageContainerStyle}>
        {renderDashboardHeader(
          "Family Member Dashboard",
          `Review providers and residents shared with you through your ${authExperience.brandName} connections.`,
          <div style={{ ...dashboardSectionCardStyle, padding: "0.75rem 1rem", boxShadow: "none" }}>
            Residents: <strong className="ml-1">{familyResidents.length}</strong>
          </div>,
        )}
        <div>
          {familyResidentsLoading ? (
            <div className="card flex justify-content-center p-5">
              <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
            </div>
          ) : (
            <>
              <div style={{ ...appAccentPanelStyle, marginBottom: "1rem" }}>
                <div style={{ fontWeight: 700, color: "#9a3412" }}>Shared resident access</div>
                <div className="mt-2" style={appMutedTextStyle}>
                  Providers can share resident access with you through Connections. Shared residents from multiple providers will appear here automatically.
                </div>
              </div>

              {sharedResidentsByProvider.length === 0 ? (
                <div style={{ ...appNeutralPanelStyle, color: "#64748b", marginBottom: "1rem" }}>
                  No residents have been shared with you yet. Ask a provider to connect with you and approve resident sharing from the Connections page.
                </div>
              ) : null}

              <div style={{ overflowX: "auto", width: "100%", marginBottom: "1rem" }}>
                <DataTable
                  value={providerRows}
                  dataKey="providerId"
                  expandedRows={expandedRows}
                  onRowToggle={(e) => setExpandedRows(e.data)}
                  rowExpansionTemplate={rowExpansionTemplate}
                  emptyMessage="No shared providers yet."
                  stripedRows
                  size="small"
                  scrollable={!mobile}
                  style={{ borderRadius: "10px", overflow: "hidden", minWidth: mobile ? "600px" : undefined }}
                >
                  <Column expander style={{ width: "3rem" }} />
                  <Column header="Provider Name" body={providerNameBody} style={{ minWidth: "12rem" }} />
                  {!mobile && <Column field="email" header="Email" style={{ minWidth: "12rem" }} />}
                  <Column header="Residents" body={residentsCountBody} style={{ width: "6rem", textAlign: "center" }} />
                </DataTable>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardFamilyMember;
